import { Calc } from "../math/Calc.js";
import { DateFormat, DateFormatValue } from "./DateFormat.js";

type LanguageCode = "de" | "en";


export class DateUtilBase {

    constructor(private _timezoneOffset: number) {
        // this.getTimezoneOffsetByName(new Date(), "Europe/Berlin");
        // this.getTimezoneOffsetByName(new Date(), "Asia/Shanghai");
        // this.getTimezoneOffsetByName(new Date(), "America/New_York");
    }

    // private getTimezoneOffsetByName(d: Date, tz: string) {
    //     const a = d.toLocaleString("ja", { timeZone: tz });//.split(/[/\s:]/);
    //     // a[1]--;
    //     // const t1 = Date.UTC.apply(null, a);
    //     // const t2 = new Date(d).setMilliseconds(0);
    //     // return (t2 - t1) / 60 / 1000;
    //     console.log(a);
    // }

    public getFullYear(d: Date): number {
        d = this.offset(d);
        return d.getUTCFullYear();
    }

    public getMonth(d: Date): number {
        d = this.offset(d);
        return d.getUTCMonth();
    }

    public getDate(d: Date): number {
        d = this.offset(d);
        return d.getUTCDate();
    }

    public getHours(d: Date): number {
        d = this.offset(d);
        return d.getUTCHours();
    }

    public getMinutes(d: Date): number {
        d = this.offset(d);
        return d.getUTCMinutes();
    }

    public getSeconds(d: Date): number {
        d = this.offset(d);
        return d.getUTCSeconds();
    }

    public offset(d: Date): Date {
        if (this._timezoneOffset === 0) {
            return d;
        }

        d = new Date(d);
        d.setUTCMinutes(d.getUTCMinutes() + this._timezoneOffset);
        return d;
    }




    /**
     * 
     * @param value A UTC date formated string like: 2019-01-15 (YYYY-MM-DD)
     * 
     * Important: without time!
     * PHP: Y-m-d
     */
    public fromString(value: string, time: string = "T00:00:00.000Z"): Date {
        return new Date(value + time);
    }

    public fromTime(time: number): Date {
        var result = new Date();
        result.setTime(time);
        return result;
    }

    public fromValues(year: number, month: number, date?: number, hours?: number, minutes?: number, seconds?: number, ms?: number): Date {
        return this.fromTime(Date.UTC(year, month, date, hours, minutes - this._timezoneOffset, seconds, ms));
    }

    /**
     * a > b = 1 
     * a == b = 0 
     * a < b = -1 
     */
    public compare(a: Date, b: Date, ignoreTime: boolean = false): number {
        if (!ignoreTime) {
            var at = a.getTime();
            var bt = b.getTime();
            if (at > bt) {
                return 1;
            }
            if (at < bt) {
                return -1;
            }
            return 0;
        }

        a = this.offset(a);
        b = this.offset(b);

        if (a.getUTCFullYear() > b.getUTCFullYear())
            return 1;
        if (a.getUTCFullYear() < b.getUTCFullYear())
            return -1;

        if (a.getUTCMonth() > b.getUTCMonth())
            return 1;
        if (a.getUTCMonth() < b.getUTCMonth())
            return -1;

        if (a.getUTCDate() > b.getUTCDate())
            return 1;
        if (a.getUTCDate() < b.getUTCDate())
            return -1;

        return 0;
    }

    /**
     * a >= start && a <= end = true 
     */
    public isBetween(a: Date, start: Date, end: Date, ignoreTime: boolean = false): boolean {
        return this.greaterEqual(a, start, ignoreTime) && this.smallerEqual(a, end, ignoreTime);
    }

    /**
     * a > b = true 
     */
    public greater(a: Date, b: Date, ignoreTime: boolean = false): boolean {
        return this.compare(a, b, ignoreTime) === 1;
    }

    /**
     * a >= b = true 
     */
    public greaterEqual(a: Date, b: Date, ignoreTime: boolean = false): boolean {
        return this.compare(a, b, ignoreTime) !== -1;
    }

    /**
     * a < b = true 
     */
    public smaller(a: Date, b: Date, ignoreTime: boolean = false): boolean {
        return this.compare(a, b, ignoreTime) === -1;
    }

    /**
     * a <= b = true 
     */
    public smallerEqual(a: Date, b: Date, ignoreTime: boolean = false): boolean {
        return this.compare(a, b, ignoreTime) !== 1;
    }

    /**
     * a == b = true 
     */
    public equal(a: Date, b: Date, ignoreTime: boolean = false): boolean {
        return this.compare(a, b, ignoreTime) === 0;
    }

    public durationToDays(duration: number): number {
        return Math.floor(duration / (1000 * 60 * 60 * 24));
    }

    public durationToHours(duration: number): number {
        return Math.floor(duration / (1000 * 60 * 60)) % 24;
    }

    public durationToMinutes(duration: number): number {
        return Math.floor(duration / (1000 * 60)) % 60;
    }

    public durationToSeconds(duration: number): number {
        return Math.floor(duration / 1000) % 60;
    }

    public clamp(d: Date, min: Date, max: Date): Date {
        var r = new Date();
        r.setTime(Calc.clamp(d.getTime(), min.getTime(), max.getTime()));
        return r;
    }

    public daysOfMonth(date: Date): number {
        return this.getDate(this.fromValues(this.getFullYear(date), this.getMonth(date) + 1, 0));
    }

    public daysOfYear(date: Date): number {
        return this.isLeapYear(date) ? 366 : 365;
    }

    public round(date: Date): Date {
        return this.fromValues(this.getFullYear(date), this.getMonth(date), this.getDate(date), 12, 0, 0, 0);
    }

    /**
     * Modified/Fixed source from Stackoverflow:
     * @see https://stackoverflow.com/questions/2536379/difference-in-months-between-two-dates-in-javascript
     */
    public diffMonths(d1: Date, d2: Date): number {
        var months = (this.getFullYear(d2) - this.getFullYear(d1)) * 12;
        months -= this.getMonth(d1);
        months += this.getMonth(d2);
        return months;
    }

    /**
     * 
     * @param a The start date.
     * @param b The end date.
     * @returns The difference in days: a - b.
     */
    public diffDays(a: Date, b: Date): number {
        var a = this.round(a);
        var b = this.round(b);
        var t = a.getTime() - b.getTime();
        const diffTime = Math.abs(t);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) * (t < 0 ? -1 : 1);
    }

    public diffYears(a: Date, b: Date): number {
        return this.getFullYear(b) - this.getFullYear(a);
    }

    public isLeapYear(date: Date): boolean {
        var year = this.getFullYear(date);
        return ((year % 4 == 0) && (year % 100 != 0)) || (year % 400 == 0);
    }

    public format(date: Date, format: DateFormatValue, language: LanguageCode = "de") {
        return DateFormat.format(this.offset(date), format, this._timezoneOffset, language);
    }
}

export const date = new DateUtilBase(-(new Date()).getTimezoneOffset());
export const dateUtc = new DateUtilBase(0);

/**
 * 
 * Use by:
 * eventfive.awi.dynamo
 * eventfive.bausenator.baul
 * eventfive.bremen.europapunkt
 * eventfive.efre.karte
 * eventfive.update
 * 
 * @deprecated Use e5.dateUtc or e5.date instead.
 */
export const DateUtil = dateUtc;

// var a = new Date("Sun May 15 2022 23:07:33 GMT+0200");
// var b = new Date("Sun May 15 2022 23:07:33 GMT+0200");

// var a = new Date();
// var b = new Date();

// b.setHours(b.getHours() + 2);

// console.log("A       ", a.toString());
// console.log("B       ", b.toString());

// console.log("A       ", date.format(a, DateFormat.DMY));
// console.log("B       ", date.format(b, DateFormat.DMY));

// console.log("A       ", date.format(a, DateFormat.ISO_8601));
// console.log("B       ", date.format(b, DateFormat.ISO_8601));

// console.log("A       ", date.format(a, DateFormat.SQL_DATETIME));
// console.log("B       ", date.format(b, DateFormat.SQL_DATETIME));